// StaffMembers.js - UI matched with Users Management page + City filter + Dynamic City Counts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaStore, FaSearch, FaCheckDouble, FaUserCircle, FaEdit } from "react-icons/fa";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import Swal from 'sweetalert2';
import './StaffMembers.css';

// Last loaded staff of each shop is kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const STAFF_CACHE_PREFIX = 'staff_cache_';
const STAFF_CITIES_PREFIX = 'staff_cities_';
const STAFF_SHOPS_KEY = 'staff_shops_cache';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getUrlShopId = () => {
  const match = window.location.pathname.match(/\/shop\/(\d+)/);
  return match ? match[1] : null;
};

const StaffMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialShopId = getUrlShopId();
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cachedStaff = initialShopId ? readJson(`${STAFF_CACHE_PREFIX}${initialShopId}`) : null;
    const cachedCities = initialShopId ? readJson(`${STAFF_CITIES_PREFIX}${initialShopId}`) : null;
    const cachedShops = readJson(STAFF_SHOPS_KEY);
    initialRef.current = {
      staff: Array.isArray(cachedStaff) ? cachedStaff : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null,
      shops: Array.isArray(cachedShops) ? cachedShops : null
    };
  }

  const [staff, setStaff] = useState(initialRef.current.staff || []);
  const [masterStaff, setMasterStaff] = useState(initialRef.current.staff || []); // ✅ Keeps all staff for accurate city counts
  const [shops, setShops] = useState(initialRef.current.shops || []);
  const [selectedShopId, setSelectedShopId] = useState(initialShopId);
  const [selectedShopName, setSelectedShopName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  // True only while there is nothing to show yet (so "No staff members found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.staff);

  // ✅ City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');

  const selectedCityRef = useRef('');
  const selectedShopNameRef = useRef('');
  const allRequestRef = useRef(0);
  const cityRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const prevShopRef = useRef(initialShopId);
  // true once there is something on the screen (a failed refresh must not blank the page)
  const hasDataRef = useRef(!!initialRef.current.staff);

  const getAuthToken = () => {
    const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
    for (let key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.access || parsed.token || parsed.access_token) {
            return parsed.access || parsed.token || parsed.access_token;
          }
          if (parsed.token && typeof parsed.token === 'object') {
            return parsed.token.access || parsed.token.token || parsed.token.access_token;
          }
        } catch (e) {
          return storedData;
        }
      }
    }
    return null;
  };

  const getUserData = () => {
    const possibleKeys = ['user', 'userData', 'currentUser', 'profile'];
    for (let key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          return JSON.parse(storedData);
        } catch (e) {
          console.warn(`Failed to parse user data from ${key}:`, e);
        }
      }
    }
    return null;
  };

  const getShopIdFromUrl = useCallback(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) return match[1];
    return null;
  }, [location.pathname]);

  // Shops list is loaded once
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch('https://super-backend.hira-tech.co.uk/user/shops/', {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          setShops(data);
          writeJson(STAFF_SHOPS_KEY, data);
          const urlShopId = getShopIdFromUrl();

          if (urlShopId) {
            setSelectedShopId(urlShopId);
          } else if (data.length > 0) {
            const userData = getUserData();
            if (userData && userData.shop_id) {
              const userShopId = userData.shop_id.toString();
              setSelectedShopId(userShopId);
              navigate(`/shop/${userShopId}/staff`, { replace: true });
            } else {
              const firstShopId = data[0].shop_id.toString();
              setSelectedShopId(firstShopId);
              navigate(`/shop/${firstShopId}/staff`, { replace: true });
            }
          } else {
            setIsLoading(false);
          }
        } else {
          if (!hasDataRef.current) setError('Failed to fetch shops');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching shops:', err);
        if (!hasDataRef.current) setError('Failed to initialize. Please try again.');
        setIsLoading(false);
      }
    };
    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlShopId = getShopIdFromUrl();
    if (urlShopId && urlShopId !== selectedShopId) {
      setSelectedShopId(urlShopId);
    }
  }, [location.pathname, selectedShopId, getShopIdFromUrl]);

  useEffect(() => {
    if (selectedShopId && shops.length > 0) {
      const shop = shops.find(s => s.shop_id.toString() === selectedShopId.toString());
      const name = shop ? shop.shop_name : 'Unknown Shop';
      setSelectedShopName(name);
      selectedShopNameRef.current = name;
    }
  }, [selectedShopId, shops]);

  const fetchFilterCities = async (shopId) => {
    if (!shopId) return;
    const requestId = ++citiesRequestRef.current;
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/shop-cities/?shop_id=${shopId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      let citiesData = [];
      if (response.ok) {
        const data = await response.json();
        if (data) {
          if (Array.isArray(data.cities)) {
            citiesData = data.cities;
          } else if (Array.isArray(data)) {
            citiesData = data;
          } else if (typeof data.cities === 'string') {
            try {
              citiesData = JSON.parse(data.cities);
            } catch {
              citiesData = data.cities.split(',').map(c => c.trim()).filter(c => c);
            }
          }
        }
      }

      if (requestId !== citiesRequestRef.current) return;
      setFilterCities(citiesData);
      writeJson(`${STAFF_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (err) {
      if (requestId !== citiesRequestRef.current) return;
      console.error('Error fetching shop cities:', err);
    }
  };

  // Fetch staff (runs in the background: only the latest request of each kind updates the screen).
  // cityFilter = null  -> all staff of the shop (master list, also saved for next time)
  // cityFilter = 'xyz' -> staff of that city (backend filter)
  const fetchStaff = useCallback(async (cityFilter = null) => {
    const isCity = !!(cityFilter && cityFilter !== '' && cityFilter !== 'all');
    const requestRef = isCity ? cityRequestRef : allRequestRef;
    const requestId = ++requestRef.current;
    const shopId = selectedShopId;

    try {
      setError(null);
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token found.');

      const params = new URLSearchParams();
      if (shopId) params.append('shop_id', shopId);
      if (isCity) {
        params.append('city', cityFilter);
      }

      const qs = params.toString();
      const apiUrl = `https://super-backend.hira-tech.co.uk/user/getAllstaff/${qs ? `?${qs}` : ''}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (requestId !== requestRef.current) return;

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (requestId !== requestRef.current) return;

      const formattedStaff = data.map(member => ({
        id: member.id,
        firstName: member.first_Name,
        lastName: member.last_Name,
        email: member.email,
        phone: member.phone_Number,
        userType: member.type,
        shopName: member.shop_name || selectedShopNameRef.current,
        shopId: member.shop_id || member.shop || shopId,
        isActive: member.is_active,
        houseNumber: member.address_details?.house_Number || 'N/A',
        streetNumber: member.address_details?.street_Number || 'N/A',
        area: member.address_details?.area || 'N/A',
        city: member.address_details?.city || 'N/A',
        fullAddress: member.address_details?.full_address || 'N/A',
        dateJoined: member.data_Joind,
        lastLogin: member.last_login
      }));

      if (!isCity) {
        // ✅ Master list (all cities) - used to calculate accurate city counts
        hasDataRef.current = true;
        setMasterStaff(formattedStaff);
        if (shopId) writeJson(`${STAFF_CACHE_PREFIX}${shopId}`, formattedStaff);
        const activeCity = selectedCityRef.current;
        setStaff(
          activeCity ? formattedStaff.filter(member => member.city === activeCity) : formattedStaff
        );
      } else if (cityFilter === selectedCityRef.current) {
        setStaff(formattedStaff);
      }

    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Error fetching staff:', err);
      if (!hasDataRef.current) setError(err.message);
    } finally {
      if (!isCity && requestId === requestRef.current) setIsLoading(false);
    }
  }, [selectedShopId]);

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Staff and cities load together instead of one after another.
  useEffect(() => {
    if (!selectedShopId) {
      setFilterCities([]);
      setSelectedFilterCity('');
      selectedCityRef.current = '';
      return;
    }
    const shopId = selectedShopId;

    if (prevShopRef.current !== shopId) {
      prevShopRef.current = shopId;
      const cachedStaff = readJson(`${STAFF_CACHE_PREFIX}${shopId}`);
      const cachedCities = readJson(`${STAFF_CITIES_PREFIX}${shopId}`);
      if (Array.isArray(cachedStaff)) {
        hasDataRef.current = true;
        setStaff(cachedStaff);
        setMasterStaff(cachedStaff);
        setIsLoading(false);
      } else {
        hasDataRef.current = false;
        setStaff([]);
        setMasterStaff([]);
        setIsLoading(true);
      }
      setFilterCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedFilterCity('');
      selectedCityRef.current = '';
      setCurrentPage(1);
    }

    fetchStaff(null);
    fetchFilterCities(shopId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId, fetchStaff]);

  // City change: the list is filtered instantly from the loaded staff,
  // and refreshed from the server in the background.
  const applyCity = (newCity) => {
    setSelectedFilterCity(newCity);
    selectedCityRef.current = newCity;
    setCurrentPage(1);
    setStaff(newCity ? masterStaff.filter(member => member.city === newCity) : masterStaff);
    if (newCity) fetchStaff(newCity);
  };

  const handleFilterCityChange = (e) => {
    applyCity(e.target.value);
  };

  const filteredStaff = staff.filter(member => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower) ||
      (member.phone && member.phone.includes(searchTerm)) ||
      (member.city && member.city.toLowerCase().includes(searchLower))
    );
  });

  const totalStaff = filteredStaff.length;
  const totalPages = Math.ceil(totalStaff / rowsPerPage);
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = filteredStaff.slice(indexOfFirstItem, indexOfLastItem);

  const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };
  const handleRowsPerPageChange = (e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(1); };

  const handleDelete = async (member) => {
    const memberName = `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'this staff member';

    const result = await Swal.fire({
      title: 'Delete Staff Member?',
      html: `Are you sure you want to delete <strong>${memberName}</strong>?<br/><small style="color:#dc3545;">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token found');

        const response = await fetch(`https://super-backend.hira-tech.co.uk/user/delete/${member.id}/`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          // the deleted row disappears right away (and from the saved copy too)
          setStaff(prev => prev.filter(m => m.id !== member.id));
          setMasterStaff(prev => {
            const next = prev.filter(m => m.id !== member.id);
            if (selectedShopId) writeJson(`${STAFF_CACHE_PREFIX}${selectedShopId}`, next);
            return next;
          });
          setCurrentPage(1);
          fetchStaff(null);

          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Staff member has been deleted successfully!',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || 'Failed to delete staff member');
        }
      } catch (err) {
        console.error('Error deleting staff:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: err.message || 'Failed to delete the staff member. Please try again.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const handleEdit = (member) => {
    navigate(`/shop/${selectedShopId}/edit-staff/${member.id}`);
  };

  const handleAddNewStaff = () => {
    navigate(`/shop/${selectedShopId}/addnewstaff`, {
      state: {
        shops,
        selectedShopId,
        selectedShopName
      }
    });
  };

  const getUserTypeBadgeClass = (type) => {
    if (!type) return 'user-type-badge default';
    return `user-type-badge ${type.toLowerCase()}`;
  };

  // ✅ Calculate total staff for "All Cities" (using master list)
  const allCitiesCount = masterStaff.length;

  // ✅ Helper to calculate count for a specific city (using master list)
  const getCityCount = (cityName) => {
    return masterStaff.filter(member => member.city === cityName).length;
  };

  if (!selectedShopId) {
    return (
      <div className="staff-members">
        <div className="staff-header-section">
          <div className="staff-header-left">
            <FaUserCircle className="staff-header-icon" />
            <h2 className="staff-header-title">Staff Members</h2>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center' }}>
          <FaStore style={{ fontSize: '64px', color: '#004aad', marginBottom: '20px' }} />
          <h3 style={{ color: '#004aad' }}>No Shop Selected</h3>
          <p style={{ color: '#666' }}>Please select a shop from the navigation menu.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="staff-members">
        <div className="staff-header-section">
          <div className="staff-header-left">
            <FaUserCircle className="staff-header-icon" />
            <h2 className="staff-header-title">Staff Members</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-members">
      {/* Header Section */}
      <div className="staff-header-section">
        <div className="staff-header-left">
          {/* ✅ FaUserCircle Icon added */}
          <FaUserCircle className="staff-header-icon" />
          <h2 className="staff-header-title">Staff Members</h2>

          {/* ✅ Dynamic City Badge with Count */}
          <span className="shop-city-badge">
            {selectedFilterCity || 'All Cities'} ({selectedFilterCity ? getCityCount(selectedFilterCity) : allCitiesCount})
          </span>
        </div>

        <div className="staff-header-right">
          {/* ✅ City Dropdown with IoMdArrowDropdown and Dynamic Counts */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={filterCities.length === 0}
            >
              {/* ✅ Dynamic "All Cities" option showing total count */}
              <option value="">
                All Cities ({allCitiesCount})
              </option>

              {/* ✅ Dynamic city options showing their respective counts */}
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({getCityCount(city)})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          {/* Search Bar */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search staff..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>

          <button className="select-all-btn" onClick={() => {}}>
            <FaCheckDouble />
            <span>Select All</span>
          </button>

          <button className="add-staff-btn" onClick={handleAddNewStaff}>
            <span>ADD NEW STAFF</span>
          </button>
        </div>
      </div>

      <div className="staff-table-container">
        <div className="staff-table-wrapper">
          <table className="staff-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No staff members found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="8" style={{ height: '200px' }}></td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((member) => (
                  <tr key={member.id}>
                    <td>{member.id}</td>
                    <td>
                      <div className="staff-name">
                        {member.firstName} {member.lastName}
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>{member.phone}</td>
                    <td>
                      <span className="user-city-cell">
                        {member.city || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={getUserTypeBadgeClass(member.userType)}>
                        {member.userType}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${member.isActive ? 'active' : 'inactive'}`}>
                        <span>{member.isActive ? 'Active' : 'Inactive'}</span>
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="actions-cell-inner">
                        <button
                          className="edit-staff-btn"
                          onClick={() => handleEdit(member)}
                          title="Edit Staff"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="delete-staff-btn"
                          onClick={() => handleDelete(member)}
                          title="Delete Staff"
                        >
                          <RiDeleteBin5Fill />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data-cell">
                    <p>
                      {selectedFilterCity
                        ? `No staff members found in ${selectedFilterCity}.`
                        : 'No staff members found.'}
                    </p>
                    {selectedFilterCity && (
                      <button
                        className="clear-city-filter-btn"
                        onClick={() => applyCity('')}
                      >
                        Clear City Filter
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination-controls">
        <div className="rows-per-page">
          <label>Rows per page: </label>
          <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="pagination">
          <span>{totalStaff > 0 ? `${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, totalStaff)} of ${totalStaff}` : '0–0 of 0'}</span>
          <button onClick={goToPreviousPage} disabled={currentPage === 1}><FaChevronLeft /> Previous</button>
          <span className="current-page">Page {currentPage} of {totalPages || 1}</span>
          <button onClick={goToNextPage} disabled={currentPage === totalPages || totalPages === 0}>Next <FaChevronRight /></button>
        </div>
      </div>
    </div>
  );
};

export default StaffMembers;